import React, { useState } from 'react';
import { FileSearch, Play, Loader2, Calendar, Users, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Code, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { reconciliationApi } from '../services/api';
import DataTable from '../components/DataTable';

// Helper to get YYYY-MM format
const getMonthString = (date) => {
  return date.toISOString().slice(0, 7);
};

// Get current and next month defaults
const now = new Date();
const currentMonth = getMonthString(now);
const nextMonth = getMonthString(new Date(now.getFullYear(), now.getMonth() + 1, 1));

export default function ReconciliationReport() {
  const [sourceMonth, setSourceMonth] = useState(currentMonth);
  const [forecastMonth, setForecastMonth] = useState(nextMonth);
  const [showQuery, setShowQuery] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Fetch forecast summary
  const { data: forecastData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['reconciliation-forecast', sourceMonth, forecastMonth],
    queryFn: async () => {
      const response = await reconciliationApi.getForecast(sourceMonth, forecastMonth);
      return response;
    },
    enabled: false,
  });

  // Fetch detail for selected task
  const { data: detailData, isLoading: loadingDetail } = useQuery({
    queryKey: ['reconciliation-detail', sourceMonth, forecastMonth, selectedTask?.tenant_code, selectedTask?.task_code],
    queryFn: async () => {
      const response = await reconciliationApi.getForecastDetail(
        sourceMonth,
        forecastMonth,
        selectedTask.tenant_code,
        selectedTask.task_code
      );
      return response;
    },
    enabled: !!selectedTask,
  });

  const handleRunForecast = () => {
    setSelectedTask(null);
    refetch();
  };

  const handleRowClick = (row) => {
    setSelectedTask(row);
  };

  const columns = [
    { key: 'tenant_code', label: 'Tenant Code', sortable: true },
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'task_code', label: 'Task Code', sortable: true },
    { key: 'task_name', label: 'Task Name', sortable: true },
    { key: 'forecast_count', label: 'Forecast', sortable: true, format: 'number' },
    { key: 'actual_count', label: 'Actual', sortable: true, format: 'number' },
    {
      key: 'auto_enrolled',
      label: 'Auto',
      sortable: true,
      format: 'number',
      render: (value) => (
        <span className="text-blue-600">{value || 0}</span>
      )
    },
    {
      key: 'user_enrolled',
      label: 'User',
      sortable: true,
      format: 'number',
      render: (value) => (
        <span className="text-purple-600">{value || 0}</span>
      )
    },
    {
      key: 'variance',
      label: 'Variance',
      sortable: true,
      format: 'number',
      render: (value) => (
        <span className={value > 0 ? 'text-red-600 font-medium' : value < 0 ? 'text-amber-600' : 'text-green-600'}>
          {value > 0 ? `+${value}` : value}
        </span>
      )
    },
  ];

  const detailColumns = [
    { key: 'consumer_code', label: 'Consumer Code', sortable: true },
    { key: 'source_status', label: 'Source Status', sortable: true },
    {
      key: 'source_enrolled_by',
      label: 'Source Enrolled By',
      sortable: true,
      render: (value) => (
        <span className={value === 'Auto' ? 'text-blue-600' : 'text-purple-600'}>
          {value}
        </span>
      )
    },
    {
      key: 'forecast_status',
      label: 'Forecast Status',
      sortable: true,
      render: (value) => (
        <span className={value === 'ENROLLED' ? 'text-green-600' : 'text-red-600 font-medium'}>
          {value}
        </span>
      )
    },
    { key: 'actual_status', label: 'Actual Status', sortable: true },
    {
      key: 'actual_enrolled_by',
      label: 'Actual Enrolled By',
      sortable: true,
      render: (value) => (
        value ? (
          <span className={value === 'Auto' ? 'text-blue-600' : 'text-purple-600'}>
            {value}
          </span>
        ) : '-'
      )
    },
  ];

  // Calculate totals
  const totalForecast = forecastData?.data?.reduce((sum, r) => sum + parseInt(r.forecast_count || 0), 0) || 0;
  const totalActual = forecastData?.data?.reduce((sum, r) => sum + parseInt(r.actual_count || 0), 0) || 0;
  const totalVariance = totalForecast - totalActual;
  const uniqueTenants = new Set(forecastData?.data?.map(r => r.tenant_code)).size || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileSearch className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reconciliation Report</h1>
          <p className="text-gray-500">Forecast recurring task enrollments and compare with actuals</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <label className="text-sm text-gray-600">Source Month:</label>
            <input
              type="month"
              value={sourceMonth}
              onChange={(e) => setSourceMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <label className="text-sm text-gray-600">Forecast Month:</label>
            <input
              type="month"
              value={forecastMonth}
              onChange={(e) => setForecastMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={handleRunForecast}
            disabled={isLoading}
            className="btn-primary flex items-center gap-2"
          >
            {isLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
            ) : (
              <><Play className="w-4 h-4" /> Run Forecast</>
            )}
          </button>
        </div>
        <p className="mt-3 text-sm text-gray-500">
          Forecast consumers from <strong>{sourceMonth}</strong> who should be enrolled in <strong>{forecastMonth}</strong> based on monthly recurring tasks.
        </p>
      </div>

      {/* Error */}
      {isError && (
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center gap-3 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>Error: {error?.message || 'Failed to fetch data'}</span>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {forecastData?.data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">Forecast</p>
                <p className="text-2xl font-bold text-blue-900">{totalForecast.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Actual</p>
                <p className="text-2xl font-bold text-green-900">{totalActual.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className={`card bg-gradient-to-br ${totalVariance > 0 ? 'from-red-50 to-red-100 border-red-200' : 'from-gray-50 to-gray-100 border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-8 h-8 ${totalVariance > 0 ? 'text-red-600' : 'text-gray-600'}`} />
              <div>
                <p className={`text-sm ${totalVariance > 0 ? 'text-red-700' : 'text-gray-700'}`}>Variance</p>
                <p className={`text-2xl font-bold ${totalVariance > 0 ? 'text-red-900' : 'text-gray-900'}`}>
                  {totalVariance > 0 ? '+' : ''}{totalVariance.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center gap-3">
              <FileSearch className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-700">Tenants</p>
                <p className="text-2xl font-bold text-purple-900">{uniqueTenants}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Query Display */}
      {forecastData?.displayQuery && (
        <div className="card">
          <button
            onClick={() => setShowQuery(!showQuery)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {showQuery ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Code className="w-4 h-4" />
            {showQuery ? 'Hide Query' : 'Show Query'}
          </button>
          {showQuery && (
            <pre className="mt-3 p-4 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
              {forecastData.displayQuery}
            </pre>
          )}
        </div>
      )}

      {/* Results Table */}
      {forecastData?.data && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Forecast by Task
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({forecastData.rowCount} tasks)
              </span>
            </h3>
            <span className="text-xs text-gray-400">
              Click a row to see consumer details
            </span>
          </div>
          <DataTable
            data={forecastData.data}
            columns={columns}
            groupBy="tenant_code"
            groupLabelKey="tenant_name"
            onRowClick={handleRowClick}
            emptyMessage="No recurring tasks found for the selected period"
          />
        </div>
      )}

      {/* Detail Panel */}
      {selectedTask && (
        <div className="card border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-amber-900">
                Consumer Forecast Detail
              </h3>
              <p className="text-sm text-amber-700">
                {selectedTask.tenant_code} - {selectedTask.task_name}
              </p>
            </div>
            <button
              onClick={() => setSelectedTask(null)}
              className="p-1 hover:bg-amber-100 rounded"
            >
              <X className="w-5 h-5 text-amber-600" />
            </button>
          </div>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
            </div>
          ) : detailData?.data ? (
            <>
              {detailData.summary && (
                <div className="flex gap-4 mb-4 text-sm">
                  <span className="px-3 py-1 bg-white rounded-full border border-amber-300">
                    Total: <strong>{detailData.summary.totalForecast}</strong>
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    Enrolled: <strong>{detailData.summary.enrolled}</strong>
                  </span>
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">
                    Not Enrolled: <strong>{detailData.summary.notEnrolled}</strong>
                  </span>
                </div>
              )}
              <DataTable
                data={detailData.data}
                columns={detailColumns}
                emptyMessage="No consumers found"
              />
            </>
          ) : (
            <p className="text-amber-700">No data available</p>
          )}
        </div>
      )}
    </div>
  );
}
